import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

const TEMPLATES = [
  { value: 'story', label: 'Story (hero + image + text — like Chairman\'s Message / Our Story)' },
  { value: 'overview', label: 'Overview (hero + two images + text, plus badges/logos/vision-mission sections — like About Us)' },
];

function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function AdminAboutPageEditor() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [template, setTemplate] = useState('story');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [heading, setHeading] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [imagePosition, setImagePosition] = useState('left');
  const [heroImage, setHeroImage] = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  const [existingHero, setExistingHero] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [existingCover, setExistingCover] = useState('');
  const [secondaryImage, setSecondaryImage] = useState(null);
  const [secondaryPreview, setSecondaryPreview] = useState(null);
  const [existingSecondary, setExistingSecondary] = useState('');
  const [body, setBody] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [metaKeywords, setMetaKeywords] = useState('');
  const [status, setStatus] = useState(1);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchPage = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        setError('Authentication required.');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${apiUrl}/api/admin/about-pages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const p = response.data.aboutPage;
        setTemplate(p.template || 'story');
        setTitle(p.title);
        setSubtitle(p.subtitle || '');
        setHeading(p.heading || '');
        setSlug(p.slug);
        setSlugTouched(true);
        setImagePosition(p.image_position || 'left');
        setExistingHero(p.hero_image || '');
        setExistingCover(p.cover_image || '');
        setExistingSecondary(p.secondary_image || '');
        setBody(p.body || '');
        setMetaTitle(p.meta_title || '');
        setMetaDescription(p.meta_description || '');
        setMetaKeywords(p.meta_keywords || '');
        setStatus(p.status);
      } else {
        setError(response.data.message || 'Failed to fetch page.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching page.');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const handleTitleChange = (value) => {
    setTitle(value);
    if (!slugTouched) {
      setSlug(slugify(value));
    }
  };

  const handleHeroChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setHeroImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setHeroPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setCoverPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSecondaryChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSecondaryImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setSecondaryPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    const formData = new FormData();
    formData.append('template', template);
    formData.append('title', title);
    formData.append('subtitle', subtitle);
    formData.append('heading', heading);
    formData.append('slug', slug);
    formData.append('image_position', imagePosition);
    formData.append('body', body);
    formData.append('meta_title', metaTitle);
    formData.append('meta_description', metaDescription);
    formData.append('meta_keywords', metaKeywords);
    formData.append('status', status);
    if (coverImage) formData.append('cover_image', coverImage);
    if (secondaryImage) formData.append('secondary_image', secondaryImage);
    if (heroImage) formData.append('hero_image', heroImage);

    try {
      const token = getToken();
      if (!token) {
        setError('Authentication required.');
        setSaving(false);
        return;
      }
      const url = isEdit ? `${apiUrl}/api/admin/about-pages/${id}` : `${apiUrl}/api/admin/about-pages`;
      const method = isEdit ? 'put' : 'post';
      const response = await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        if (isEdit) {
          setSuccess('Page updated successfully.');
          fetchPage();
        } else {
          navigate(`/admin-about-pages/edit/${response.data.id}`);
        }
      } else {
        setError(response.data.message || 'Failed to save page.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving page.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li>
                  <Link to="/admin-about-pages" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Other Pages
                  </Link>
                </li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">{isEdit ? 'Edit' : 'Add New'}</span></li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {loading ? (
            <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Template</h5>
                </div>
                <div className="card-body">
                  <div className="row g-20">
                    {TEMPLATES.map((t) => (
                      <div className="col-12" key={t.value}>
                        <div className="form-check">
                          <input
                            type="radio"
                            className="form-check-input"
                            id={`template-${t.value}`}
                            name="template"
                            value={t.value}
                            checked={template === t.value}
                            onChange={(e) => setTemplate(e.target.value)}
                            disabled={saving || isEdit}
                          />
                          <label className="form-check-label" htmlFor={`template-${t.value}`}>
                            {t.label}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                  {isEdit && (
                    <span className="text-13 text-gray-400 d-block mt-8">
                      Template can't be changed after creation — create a new page instead if you need a different layout.
                    </span>
                  )}
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Page Details</h5>
                </div>
                <div className="card-body">
                  <div className="row g-20">
                    <div className="col-md-8">
                      <label htmlFor="title" className="h5 mb-8 fw-semibold font-heading">
                        Title (hero heading) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="title"
                        className="form-control py-11"
                        value={title}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        disabled={saving}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="status" className="h5 mb-8 fw-semibold font-heading">
                        Status
                      </label>
                      <select
                        id="status"
                        className="form-select py-11"
                        value={status}
                        onChange={(e) => setStatus(Number(e.target.value))}
                        disabled={saving}
                      >
                        <option value={1}>Published</option>
                        <option value={0}>Draft</option>
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="subtitle" className="h5 mb-8 fw-semibold font-heading">
                        Subtitle <span className="text-gray-400 fw-normal text-13">(small label above/near the title)</span>
                      </label>
                      <input
                        type="text"
                        id="subtitle"
                        className="form-control py-11"
                        value={subtitle}
                        onChange={(e) => setSubtitle(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="heading" className="h5 mb-8 fw-semibold font-heading">
                        Section Heading <span className="text-gray-400 fw-normal text-13">(bigger headline above the body text)</span>
                      </label>
                      <input
                        type="text"
                        id="heading"
                        className="form-control py-11"
                        value={heading}
                        onChange={(e) => setHeading(e.target.value)}
                        disabled={saving}
                      />
                    </div>

                    <div className="col-md-8">
                      <label htmlFor="slug" className="h5 mb-8 fw-semibold font-heading">
                        Path <span className="text-red-500">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">ifmedu.in/</span>
                        <input
                          type="text"
                          id="slug"
                          className="form-control py-11"
                          value={slug}
                          onChange={(e) => { setSlug(slugify(e.target.value)); setSlugTouched(true); }}
                          disabled={saving}
                          required
                        />
                      </div>
                      <span className="text-13 text-gray-400">Lowercase letters, numbers and hyphens only. Auto-filled from the title, but you can override it.</span>
                    </div>
                    {template === 'story' && (
                      <div className="col-md-4">
                        <label htmlFor="imagePosition" className="h5 mb-8 fw-semibold font-heading">
                          Image Position
                        </label>
                        <select
                          id="imagePosition"
                          className="form-select py-11"
                          value={imagePosition}
                          onChange={(e) => setImagePosition(e.target.value)}
                          disabled={saving}
                        >
                          <option value="left">Standard</option>
                          <option value="right">Reversed</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Images</h5>
                </div>
                <div className="card-body">
                  <div className="row g-20">
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading d-block">
                        Hero Background Image <span className="text-gray-400 fw-normal text-13">(optional — behind the page title)</span>
                      </label>
                      <div className="d-flex align-items-center gap-16">
                        {(heroPreview || existingHero) ? (
                          <img
                            src={heroPreview || `${apiUrl}/${existingHero}`}
                            alt="hero preview"
                            style={{ width: 120, height: 90, borderRadius: 12, objectFit: 'cover', border: '3px solid #e8f5ee' }}
                          />
                        ) : (
                          <div style={{
                            width: 120, height: 90, borderRadius: 12, background: '#f3f4f6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #d1d5db'
                          }}>
                            <i className="ph ph-image text-gray-400" style={{ fontSize: 28 }} />
                          </div>
                        )}
                        <input
                          type="file"
                          className="form-control py-11"
                          accept="image/*"
                          onChange={handleHeroChange}
                          disabled={saving}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading d-block">
                        {template === 'overview' ? 'Cover Image (first)' : 'Content Image'}
                      </label>
                      <div className="d-flex align-items-center gap-16">
                        {(coverPreview || existingCover) ? (
                          <img
                            src={coverPreview || `${apiUrl}/${existingCover}`}
                            alt="cover preview"
                            style={{ width: 120, height: 90, borderRadius: 12, objectFit: 'cover', border: '3px solid #e8f5ee' }}
                          />
                        ) : (
                          <div style={{
                            width: 120, height: 90, borderRadius: 12, background: '#f3f4f6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #d1d5db'
                          }}>
                            <i className="ph ph-image text-gray-400" style={{ fontSize: 28 }} />
                          </div>
                        )}
                        <input
                          type="file"
                          className="form-control py-11"
                          accept="image/*"
                          onChange={handleCoverChange}
                          disabled={saving}
                        />
                      </div>
                    </div>
                    {template === 'overview' && (
                      <div className="col-md-6">
                        <label className="h5 mb-8 fw-semibold font-heading d-block">Secondary Image (overlay)</label>
                        <div className="d-flex align-items-center gap-16">
                          {(secondaryPreview || existingSecondary) ? (
                            <img
                              src={secondaryPreview || `${apiUrl}/${existingSecondary}`}
                              alt="secondary preview"
                              style={{ width: 120, height: 90, borderRadius: 12, objectFit: 'cover', border: '3px solid #e8f5ee' }}
                            />
                          ) : (
                            <div style={{
                              width: 120, height: 90, borderRadius: 12, background: '#f3f4f6',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #d1d5db'
                            }}>
                              <i className="ph ph-image text-gray-400" style={{ fontSize: 28 }} />
                            </div>
                          )}
                          <input
                            type="file"
                            className="form-control py-11"
                            accept="image/*"
                            onChange={handleSecondaryChange}
                            disabled={saving}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">Body Content</h5>
                </div>
                <div className="card-body">
                  <CKEditor
                    editor={ClassicEditor}
                    data={body}
                    onChange={(event, editor) => setBody(editor.getData())}
                    config={{
                      toolbar: [
                        'heading', '|',
                        'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
                        'blockQuote', 'insertTable', '|',
                        'undo', 'redo'
                      ]
                    }}
                  />
                </div>
              </div>

              <div className="card mb-24">
                <div className="card-header border-bottom border-gray-100">
                  <h5 className="mb-0">SEO / Meta Tags</h5>
                </div>
                <div className="card-body">
                  <div className="row g-20">
                    <div className="col-12">
                      <label htmlFor="metaTitle" className="h5 mb-8 fw-semibold font-heading">Meta Title</label>
                      <input
                        type="text"
                        id="metaTitle"
                        className="form-control py-11"
                        value={metaTitle}
                        onChange={(e) => setMetaTitle(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="metaDescription" className="h5 mb-8 fw-semibold font-heading">Meta Description</label>
                      <textarea
                        id="metaDescription"
                        className="form-control py-11"
                        rows={3}
                        value={metaDescription}
                        onChange={(e) => setMetaDescription(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="col-12">
                      <label htmlFor="metaKeywords" className="h5 mb-8 fw-semibold font-heading">Meta Keywords</label>
                      <input
                        type="text"
                        id="metaKeywords"
                        className="form-control py-11"
                        placeholder="comma, separated, keywords"
                        value={metaKeywords}
                        onChange={(e) => setMetaKeywords(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end gap-8 mb-24">
                <Link to="/admin-about-pages" className="btn btn-outline-main rounded-pill py-9">Cancel</Link>
                <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                  {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Page'}
                </button>
              </div>
            </form>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminAboutPageEditor;
